import { useSelector } from 'react-redux';
import { EditListingPageState } from '../EditListingPage.duck';
import { useOwnListing } from '../../../hooks/useOwnListing';
import { getAllListingAttributes } from '../EditListingPage.utils';
import { Uuid } from '../../../types/sharetribe/uuid';
import { OwnListingWithImages, SharetribeImage } from '../../../types/sharetribe/listing';

export type CurrentListingType = {
  id: Uuid | null;
  attributes: Partial<OwnListingWithImages['attributes']>;
  images: SharetribeImage[];
};

/**
 * Returns the current effective listing & saved listing for the edit listing flow.
 *
 * Saved Listing: only returns values that are saved in Sharetribe (unavailable for guest listings).
 *   Will return an empty Sharetribe listing object if no listing saved:
 *    { id: null, type: 'ownListing', attributes: { publicData: {} },images: [] }
 *   Use savedListing when you want to pull a value that has definitely
 *   been saved to the Sharetribe listing (i.e. comparing saved and unsaved values).
 *
 * Current Listing: a Sharetribe listing-like object that includes the combined values of
 *   any saved Sharetribe values for the listing being edited (if available), and values from
 *   all the listing flow form fields (Search, Item Details).
 *   If entities have overlapping attributes, the unsaved value gets returned.
 *   Use this value in the Edit Listing flow for any logic around listing attributes and to
 *   set initial values in the form fields.
 *   In most cases, this is the value to use vs. savedListing.
 *
 * @returns {Object} { savedListing, currentListing }
 */
export const useOwnListingVersions = (): {
  savedListing: OwnListingWithImages;
  currentListing: CurrentListingType;
} => {
  const { params, unsavedListingValues } = useSelector<any>(
    (state) => state.EditListingPage
  ) as EditListingPageState;

  const savedListing = useOwnListing(params?.id);

  const currentListing = {
    id: savedListing?.id, // Will be null if no listing has been saved.
    attributes: getAllListingAttributes(savedListing, unsavedListingValues),
    // Native `images` are no longer supported, so adding this here
    // for legacy support when editing old listings.
    images: savedListing?.images,
  };

  return { savedListing, currentListing };
};
